import type { ReactElement } from 'react';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

export interface BookDocumentRow {
  id: string;
  kind: string;
  status: string;
  periodId: string;
  amount: number;
  direction: string;
  partyId?: string;
  volumeRunId?: string;
  accountCode?: string;
  afeNumber?: string;
  pricingStatus?: string;
  missingQuoteDates?: string[];
  lines?: { partyId: string; workingInterest: string; share: number }[];
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'kind', label: 'Kind', header: 'Kind' },
  { id: 'periodId', label: 'Period', header: 'Period' },
  { id: 'amount', label: 'Amount', header: 'Amount' },
  { id: 'direction', label: 'Dir', header: 'Dir' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'accountCode', label: 'Account', header: 'Account' },
  { id: 'pricingStatus', label: 'Pricing', header: 'Pricing' }
];

function groupValue(row: BookDocumentRow, columnId: string): string {
  switch (columnId) {
    case 'kind':
      return row.kind;
    case 'periodId':
      return row.periodId;
    case 'amount':
      return String(row.amount);
    case 'direction':
      return row.direction;
    case 'status':
      return row.status;
    case 'accountCode':
      return row.accountCode?.trim() || 'Uncoded';
    case 'pricingStatus':
      return row.pricingStatus?.trim() || '—';
    default:
      return '—';
  }
}

interface BooksDocumentsGridProps {
  rows: BookDocumentRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew?: () => void;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function BooksDocumentsGrid({
  rows,
  selectedId,
  onSelect,
  onNew,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: BooksDocumentsGridProps): ReactElement {
  return (
    <section className="card requests-grid-card">
      <div className="row spread requests-grid-toolbar">
        <h2>Books documents</h2>
        {onNew && (
          <button type="button" onClick={onNew} data-testid="books-new">
            + New document
          </button>
        )}
      </div>
      <p className="hint">Click a row for the side panel. Sales AR and royalty AP stay separate.</p>
      {loading && <p className="hint">Loading books…</p>}
      <GroupedDataGrid<BookDocumentRow>
        columns={COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={groupValue}
        colSpan={7}
        emptyMessage="No books documents match this kind."
        renderRow={(row) => (
          <tr
            className={selectedId === row.id ? 'selected' : undefined}
            onClick={() => onSelect(row.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(row.id);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <td>{row.kind}</td>
            <td className="mono-id">{row.periodId}</td>
            <td>{row.amount}</td>
            <td>{row.direction}</td>
            <td>
              <span className="status-pill">{row.status}</span>
            </td>
            <td className="mono-id">
              {row.accountCode?.trim() ? (
                row.accountCode
              ) : (
                <span className="status-pill">Uncoded</span>
              )}
            </td>
            <td>
              {row.pricingStatus?.trim() ? (
                <span className="status-pill">{row.pricingStatus}</span>
              ) : (
                '—'
              )}
            </td>
          </tr>
        )}
      />
      <GridPaginationBar
        idPrefix="books-docs"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
